import type { EnvVar } from '@/api/schemas';
import { formatDateTime, timeAgo } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { ScopeChip, Tag, VisibilityBadge } from '@/components/badges';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RowMenu } from './EnvVarRowMenu';
import { WarningChips } from './EnvVarWarningChips';
import { derivedWarnings } from './envHelpers';
import type { EnvVarActions } from './EnvVarsTable';

interface RowProps {
  v: EnvVar;
  now: number;
}

/**
 * Warnings sit under the key in every layout (their testids must stay unique). From 1440 (xl) visibility, type,
 * version, updated and fingerprint get their own columns; below, they stack into two columns like the builds table.
 */
export function EnvVarsDesktopTable({ vars, now, actions }: { vars: EnvVar[]; now: number; actions: EnvVarActions }) {
  return (
    <Table
      aria-label="Environment variables"
      className="table-fixed sm:[&_td]:px-3 sm:[&_th]:px-3 sm:[&_td:first-child]:pl-5 sm:[&_th:first-child]:pl-5 sm:[&_td:last-child]:pr-5 sm:[&_th:last-child]:pr-5"
    >
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>Key</TableHead>
          <TableHead className="w-[26%] xl:w-[11%]">Visibility</TableHead>
          <TableHead className="w-[11%] max-xl:hidden">Type</TableHead>
          <TableHead className="w-[7%] max-xl:hidden">Version</TableHead>
          <TableHead className="w-[11%] max-xl:hidden">Updated</TableHead>
          <TableHead className="w-[24%] xl:w-[16%]">Changed</TableHead>
          <TableHead className="w-14">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {vars.map((v, i) => (
          <EnvVarRow key={v.id} v={v} i={i} now={now} actions={actions} />
        ))}
      </TableBody>
    </Table>
  );
}

function EnvVarRow({ v, i, now, actions }: RowProps & { i: number; actions: EnvVarActions }) {
  return (
    <TableRow data-testid={`env-row-${v.key}`} style={stagger(Math.min(i, 12))} className={cn('enter', v.inherited && 'opacity-80')}>
      <KeyCell v={v} />
      <VisibilityCell v={v} />
      <TypeCell v={v} />
      <TableCell className="tabular py-2 font-mono text-xs text-muted-foreground max-xl:hidden">{v.currentVersion ? `v${v.currentVersion.version}` : '—'}</TableCell>
      <UpdatedCell v={v} now={now} />
      <FingerprintCell v={v} now={now} />
      <TableCell className="py-2 text-right">
        <RowMenu v={v} {...actions} />
      </TableCell>
    </TableRow>
  );
}

function KeyCell({ v }: { v: EnvVar }) {
  return (
    <TableCell className="whitespace-normal py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate font-mono text-[13px] font-medium text-foreground" title={v.key}>
          {v.key}
        </span>
        {v.inherited ? <ScopeChip scope={v.scope} environment={v.environment} platform={v.platform} className="shrink-0 text-subtle" /> : null}
      </div>
      {v.description ? (
        <div className="truncate text-xs text-muted-foreground" title={v.description}>
          {v.description}
        </div>
      ) : null}
      {derivedWarnings(v).length > 0 ? (
        <div className="mt-1">
          <WarningChips v={v} />
        </div>
      ) : null}
    </TableCell>
  );
}

function VisibilityCell({ v }: { v: EnvVar }) {
  return (
    <TableCell className="overflow-hidden whitespace-normal py-2">
      <div className="flex flex-wrap items-center gap-1">
        <VisibilityBadge visibility={v.visibility} />
        <span className="flex flex-wrap items-center gap-1 xl:hidden">
          <Tag>{v.valueType}</Tag>
          {v.configTime ? <Tag title="Also used when the app config is read">app config</Tag> : null}
          {v.currentVersion ? <Tag>v{v.currentVersion.version}</Tag> : null}
        </span>
      </div>
    </TableCell>
  );
}

function TypeCell({ v }: { v: EnvVar }) {
  return (
    <TableCell className="py-2 max-xl:hidden">
      <span className="flex items-center gap-1">
        <Tag>{v.valueType}</Tag>
        {v.configTime ? (
          <Tag title="Also used when the app config is read">
            <Icon name="settings" size={11} />
          </Tag>
        ) : null}
      </span>
    </TableCell>
  );
}

function UpdatedCell({ v, now }: RowProps) {
  return (
    <TableCell className="py-2 font-mono text-xs text-subtle max-xl:hidden">
      <time dateTime={v.updatedAt} title={formatDateTime(v.updatedAt)}>
        {timeAgo(v.updatedAt, now)}
      </time>
    </TableCell>
  );
}

function FingerprintCell({ v, now }: RowProps) {
  const fingerprint = v.currentVersion?.valueFingerprint;
  return (
    <TableCell className="overflow-hidden py-2 font-mono text-xs text-muted-foreground">
      <span className="block truncate" title={fingerprint ? `Value fingerprint ${fingerprint}` : undefined}>
        {v.currentVersion ? `v${v.currentVersion.version}` : '—'}
      </span>
      <time dateTime={v.updatedAt} title={formatDateTime(v.updatedAt)} className="block truncate text-[11px] text-subtle">
        {timeAgo(v.updatedAt, now)}
      </time>
    </TableCell>
  );
}
